import { Check, ChevronDown, Search, X } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
} from '@/components/ui/drawer'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import { useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'

export interface SearchableSelectOption {
	value: string
	label: string
}

interface SearchableSelectProps {
	options: SearchableSelectOption[]
	value?: string
	onValueChange?: (value: string) => void
	placeholder?: string
	searchPlaceholder?: string
	emptyMessage?: string
	disabled?: boolean
	className?: string
	nameField?: string
	id?: string
}

type SearchableSelectRef = HTMLButtonElement

/* ─── Shared option list ─── */

function OptionList({
	options,
	search,
	onSearchChange,
	value,
	onSelect,
	searchPlaceholder,
	emptyMessage,
	stretch = false,
}: {
	options: SearchableSelectOption[]
	search: string
	onSearchChange: (v: string) => void
	value?: string
	onSelect: (v: string) => void
	searchPlaceholder: string
	emptyMessage: string
	stretch?: boolean
}) {
	const inputRef = React.useRef<HTMLInputElement>(null)

	const filtered = React.useMemo(() => {
		const term = search.trim().toLowerCase()
		if (!term) return options
		return options.filter((opt) => opt.label.toLowerCase().includes(term))
	}, [options, search])

	return (
		<div className={cn('flex flex-col', stretch && 'h-full')}>
			{/* Search bar */}
			<div className="border-b px-3 py-2">
				<div className="flex items-center gap-2">
					<Search className="text-muted-foreground size-4 shrink-0" />
					<input
						ref={inputRef}
						type="text"
						placeholder={searchPlaceholder}
						value={search}
						onChange={(e) => onSearchChange(e.target.value)}
						className="text-foreground placeholder:text-muted-foreground w-full bg-transparent text-sm outline-none"
					/>
					{search && (
						<button
							type="button"
							onClick={() => onSearchChange('')}
							className="text-muted-foreground hover:text-foreground shrink-0"
							aria-label="Clear search"
						>
							<X className="size-3.5" />
						</button>
					)}
				</div>
			</div>

			{/* Options */}
			<div
				className={cn(
					'overflow-y-auto overscroll-contain py-1',
					stretch ? 'flex-1' : 'max-h-60 sm:max-h-52',
				)}
			>
				{filtered.length === 0 ? (
					<p className="text-muted-foreground px-3 py-8 text-center text-sm">
						{emptyMessage}
					</p>
				) : (
					filtered.map((opt) => {
						const isSelected = opt.value === value
						return (
							<button
								key={opt.value}
								type="button"
								onClick={() => onSelect(opt.value)}
								className={cn(
									'flex w-full cursor-pointer items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors sm:py-2',
									'hover:bg-accent hover:text-accent-foreground',
									'active:bg-accent/80',
									isSelected && 'bg-accent/60 font-medium',
								)}
							>
								<Check
									className={cn(
										'size-4 shrink-0',
										isSelected
											? 'text-kaefer-dark-grey opacity-100'
											: 'opacity-0',
									)}
								/>
								<span className="truncate">{opt.label}</span>
							</button>
						)
					})
				)}
			</div>
		</div>
	)
}

/* ─── Main component ─── */

const SearchableSelect = React.forwardRef<
	SearchableSelectRef,
	SearchableSelectProps
>(
	(
		{
			options,
			value,
			onValueChange,
			placeholder = 'Select...',
			searchPlaceholder = 'Search...',
			emptyMessage = 'No results found.',
			disabled = false,
			className,
			nameField,
			id,
		},
		ref,
	) => {
		const [open, setOpen] = React.useState(false)
		const [search, setSearch] = React.useState('')
		const isDesktop = useMediaQuery('(min-width: 640px)')

		const selectedLabel = options.find((o) => o.value === value)?.label

		const handleSelect = (optValue: string) => {
			onValueChange?.(optValue === value ? '' : optValue)
			setOpen(false)
			setSearch('')
		}

		const handleOpenChange = (next: boolean) => {
			setOpen(next)
			if (!next) setSearch('')
		}

		const triggerButton = (
			<Button
				ref={ref}
				id={id}
				name={nameField}
				variant="outline"
				type="button"
				role="combobox"
				aria-expanded={open}
				disabled={disabled}
				className={cn(
					'h-9 w-full justify-between px-3 font-normal hover:bg-transparent hover:text-muted-foreground',
					!selectedLabel && 'text-muted-foreground',
					className,
				)}
			>
				<span className="truncate">{selectedLabel ?? placeholder}</span>
				<ChevronDown
					className={cn(
						'ml-2 size-4 shrink-0 opacity-50 transition-transform',
						open && 'rotate-180',
					)}
				/>
			</Button>
		)

		const optionListDesktop = (
			<OptionList
				options={options}
				search={search}
				onSearchChange={setSearch}
				value={value}
				onSelect={handleSelect}
				searchPlaceholder={searchPlaceholder}
				emptyMessage={emptyMessage}
			/>
		)

		const optionListMobile = (
			<OptionList
				options={options}
				search={search}
				onSearchChange={setSearch}
				value={value}
				onSelect={handleSelect}
				searchPlaceholder={searchPlaceholder}
				emptyMessage={emptyMessage}
				stretch
			/>
		)

		/* Desktop → Popover */
		if (isDesktop) {
			return (
				<Popover open={open} onOpenChange={handleOpenChange}>
					<PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
					<PopoverContent
						className="w-(--radix-popover-trigger-width) p-0"
						align="start"
					>
						{optionListDesktop}
					</PopoverContent>
				</Popover>
			)
		}

		/* Mobile → Drawer */
		return (
			<>
				<span onClick={() => !disabled && setOpen(true)}>{triggerButton}</span>
				<Drawer
					open={open}
					onOpenChange={handleOpenChange}
					repositionInputs={false}
				>
					<DrawerContent className="flex h-3/5 flex-col">
						<DrawerHeader className="pb-2">
							<DrawerTitle className="text-base">{placeholder}</DrawerTitle>
							<DrawerClose className="absolute top-4 right-4">
								<X className="text-muted-foreground size-5" />
							</DrawerClose>
						</DrawerHeader>
						<div className="min-h-0 flex-1 overflow-hidden px-2 pb-4">
							{optionListMobile}
						</div>
					</DrawerContent>
				</Drawer>
			</>
		)
	},
)

SearchableSelect.displayName = 'SearchableSelect'

export { SearchableSelect }
